import { useMemo } from 'react';

import { DataType } from '@revelio/core';

import { countSubsidiariesAt } from '../../Chart/SubsidiaryTrend/build-subsidiary-trends';
import { buildSubsidiaryLineage } from '../../Chart/Timeline/build-subsidiary-lineage';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  type SingleDataSet,
} from '../types';
import { combineQueryStatus } from '../utils/data-provider-results-utils';
import { useDescendantCounts } from './company-mapper-queries';
import { useLineageRows } from './SubsidiaryLineageDataProvider';
import { usePrimaryCompanies } from './use-primary-company';
import { useSnapshotDateEnd } from './use-subsidiary-dates';

/**
 * Group-structure counts for every company chip: today's from company-mapper,
 * or at the end of the selected month from the dated ownership links when the
 * Date filter is set. They stay on the whole group when the tree is drilled into.
 */
export const SubsidiaryMetricsDataProvider = ({
  children,
}: {
  children: DataProviderChildren<SingleDataSet>;
}) => {
  const companies = usePrimaryCompanies();
  const at = useSnapshotDateEnd();
  const rcids = useMemo(
    () => companies.map((company) => company.rcid),
    [companies]
  );
  const noRcids = useMemo((): number[] => [], []);
  const { counts, statuses, error } = useDescendantCounts(
    at === null ? rcids : noRcids
  );
  const { rows, statuses: lineageStatuses } = useLineageRows(
    at === null ? noRcids : rcids
  );

  const dataSet = useMemo<SingleDataSet | undefined>(() => {
    const values =
      at === null
        ? counts
        : companies.map((company, index) => {
            const companyRows = rows[index];
            return companyRows === undefined
              ? undefined
              : countSubsidiariesAt({
                  data: buildSubsidiaryLineage({
                    rows: companyRows,
                    root: company,
                    companies: [],
                  }),
                  at,
                });
          });
    if (values.some((value) => value === undefined)) {
      return undefined;
    }
    return {
      type: DataType.SINGLE,
      data: companies.map((company, index) => ({
        id: String(company.rcid),
        dim: company.name,
        value: values[index] ?? null,
      })),
    };
  }, [at, companies, counts, rows]);

  const status = combineQueryStatus(at === null ? statuses : lineageStatuses);
  return children({
    data: dataSet,
    status: companies.length === 0 ? DataProviderQueryStatus.Success : status,
    ...(error != null && { error }),
  });
};
